/**
 * Optimized HomePage Component
 * Prevents unnecessary re-renders when cart updates
 */

import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth.js";
import { useCart } from "../hooks/useCart.js";
import { useBrands } from "../hooks/useProducts.js";
import { formatCurrency } from "../utils/formatters.js";
import { apiService, tokenManager } from "../services/api.js";
import LoadingSpinner from "@/components/common/LoadingSpinner.jsx";
import Sidebar from "@/components/layout/Sidebar.jsx";
import HeroSlider from "@/components/home/HeroSlider.jsx";
import HeroItemsGrid from "../components/home/HeroItemsGrid.jsx";
import BrandProductSection from "../components/products/BrandProductSection.jsx";

const HomePage = () => {
  const { isAuthenticated, company, getUserDisplayName } = useAuth();
  const { getCartTotal, getCartItemCount } = useCart();

  // Get all brands (sorted by displayOrder from BrandCompanyMappings)
  const { data: brands = [], isLoading, error } = useBrands();

  // Hero images state
  const [heroItems, setHeroItems] = useState([]);

  // Fetch hero images from API
  useEffect(() => {
    const fetchHeroImages = async () => {
      try {
        const company = tokenManager.getCurrentCompany();
        if (!company?.id) return;

        const response = await apiService.get('/storefront/HeroImages', {
          $filter: `CompanyID eq ${company.id}`,
          $orderby: 'DisplayOrder asc',
          $expand: 'Image'
        });

        if (response?.value) {
          setHeroItems(response.value);
        }
      } catch (err) {
        console.error('Failed to fetch hero images:', err);
      }
    };

    fetchHeroImages();
  }, [company?.id]);

  // ✅ OPTIMIZATION: Memoize cart totals so they don't cause re-renders
  const cartTotal = useMemo(() => getCartTotal(), [getCartTotal]);
  const cartItemCount = useMemo(() => getCartItemCount(), [getCartItemCount]);

  // ✅ OPTIMIZATION: Memoize user display data
  const userDisplayData = useMemo(
    () => ({
      displayName: getUserDisplayName(),
      companyName: company?.displayName || company?.name,
      isAuthenticated,
    }),
    [
      getUserDisplayName,
      company?.displayName,
      company?.name,
      isAuthenticated,
    ],
  );
  if (isLoading) {
    return (
      <div className="container py-5 text-center">
        <LoadingSpinner />
        <p className="mt-3 text-muted">Loading brands...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          <h4>Error Loading Homepage</h4>
          <p>{error.message || "Failed to load brands"}</p>
          <button
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="homepage">
      <div className="container-fluid">
        
         
          {/* Main Content */}
          <main className="homepage__main ">
            <div className="homepage__content pt-md-4">
              {/* Hero Slider - Above the fold */}
              {/*
              <section className="homepage__hero">
                <HeroSlider slides={heroSlides} />
              </section>
              */}
              {/* Hero Images Grid - Above the fold */}
              {heroItems.length > 0 && (
                <section className="homepage__hero-items mb-4">
                  <HeroItemsGrid heroItems={heroItems} />
                </section>
              )}
              {/*
              <section className="">
                <BrandCarousel speed={120} />
              </section>
             */}

             
              {/* Product Brands */}
              <div className="homepage__categories">
                {brands.length > 0 ? (
                  brands.map((brand, index) => (
                    <MemoizedBrandProductSection
                      key={brand.id}
                      brand={brand}
                      index={index}
                    />
                  ))
                ) : (
                  <div className="homepage__no-categories">
                    <div className="text-center py-5">
                      <i className="bi bi-grid display-1 text-muted"></i>
                      <h3 className="mt-3 text-muted">
                        No Brands Available
                      </h3>
                      <p className="text-muted">
                        Please contact support if you believe this is an error.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </main>
        
      </div>
    </div>
  );
};

// ✅ OPTIMIZATION: Memoized components to prevent re-renders

//const MemoizedSidebar = React.memo(() => <Sidebar />);

const MemoizedBrandProductSection = React.memo(
  ({ brand, index }) => (
    <BrandProductSection brand={brand} index={index} />
  ),
  (prevProps, nextProps) => {
    return (
      prevProps.brand.id === nextProps.brand.id &&
      prevProps.index === nextProps.index
    );
  },
);

const WelcomeSection = React.memo(
  ({ displayName, companyName, categoriesCount, cartTotal, cartItemCount }) => (
    <div className="homepage__welcome">
      <div className="row align-items-center">
        <div className="col-md-8">
          <h1 className="homepage__welcome-title">
            Welcome back, {displayName}!
          </h1>
          <p className="homepage__welcome-subtitle">
            Browse our wholesale catalog across {categoriesCount} categories
            {companyName && (
              <>
                {" "}
                for{" "}
                <span className="homepage__company-name">{companyName}</span>
              </>
            )}
          </p>
        </div>
        <div className="col-md-4 text-md-end">
          <div className="homepage__quick-actions">
            <Link
              to="/quick-order"
              className="btn btn-warning homepage__quick-order-btn"
            >
              <i className="bi bi-lightning me-2"></i>
              Quick Order
            </Link>
            <div className="homepage__cart-summary">
              Cart:{" "}
              <span className="homepage__cart-total">
                {formatCurrency(cartTotal)}
              </span>
              {cartItemCount > 0 && <> ({cartItemCount} items)</>}
            </div>
          </div>
        </div>
      </div>
    </div>
  ),
);

const QuickStatsSection = React.memo(
  ({ cartTotal, cartItemCount, categoriesCount }) => (
    <div className="homepage__stats">
      <div className="row">
        <div className="col-md-3 mb-3">
          <div className="homepage__stat-card homepage__stat-card--cart">
            <div className="homepage__stat-icon">
              <i className="bi bi-cart3"></i>
            </div>
            <div className="homepage__stat-content">
              <h6 className="homepage__stat-title">Current Cart</h6>
              <div className="homepage__stat-value">
                {formatCurrency(cartTotal)}
              </div>
              <small className="homepage__stat-meta">
                {cartItemCount} items
              </small>
            </div>
          </div>
        </div>

        <div className="col-md-3 mb-3">
          <div className="homepage__stat-card homepage__stat-card--categories">
            <div className="homepage__stat-icon">
              <i className="bi bi-grid"></i>
            </div>
            <div className="homepage__stat-content">
              <h6 className="homepage__stat-title">Categories</h6>
              <div className="homepage__stat-value">{categoriesCount}</div>
              <small className="homepage__stat-meta">Available</small>
            </div>
          </div>
        </div>

        <div className="col-md-3 mb-3">
          <div className="homepage__stat-card homepage__stat-card--quick-order">
            <div className="homepage__stat-icon">
              <i className="bi bi-lightning"></i>
            </div>
            <div className="homepage__stat-content">
              <h6 className="homepage__stat-title">Quick Actions</h6>
              <Link
                to="/quick-order"
                className="btn btn-light btn-sm homepage__stat-action"
              >
                Quick Order
              </Link>
            </div>
          </div>
        </div>

        <div className="col-md-3 mb-3">
          <div className="homepage__stat-card homepage__stat-card--orders">
            <div className="homepage__stat-icon">
              <i className="bi bi-clock-history"></i>
            </div>
            <div className="homepage__stat-content">
              <h6 className="homepage__stat-title">Order History</h6>
              <Link
                to="/orders"
                className="btn btn-dark btn-sm homepage__stat-action"
              >
                View History
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  ),
);

// Set display names for debugging

MemoizedBrandProductSection.displayName = "MemoizedBrandProductSection";
WelcomeSection.displayName = "WelcomeSection";
QuickStatsSection.displayName = "QuickStatsSection";

export default HomePage;
